/**
 * Cryptocurrency Module
 * Provides data access interfaces for cryptocurrencies
 */

import { Client, ClientOptions } from '../../utils/client';
import { convertKlinePeriod } from '../../utils';
import { APIResponse } from '../../types'
import { TickData, TickDataMap } from '../../types/tick';
import { QuoteData, QuoteDataMap } from '../../types/quote';
import { DepthData, DepthDataMap } from '../../types/depth';
import { KlineData, KlineDataMap, GetKlineOptions, GetKlinesOptions } from '../../types/kline';
import { CreateSocketOptions } from '../../types/socket';
import SocketClient from '../../utils/SocketClient';

/**
 * Cryptocurrency SDK Client
 * Specialized for cryptocurrency data access
 */
export class CryptoClient extends Client {
    constructor(token: string, options?: ClientOptions) {
        super(token, options);
    }
    /**
     * Get Cryptocurrency Real-Time Trade Snapshot
     * @description Query latest trade real-time market data for cryptocurrency based on market code and symbol code
     * @param params - Query parameters object
     * @param params.region - Market code (e.g., BA, BT, PB etc.), see {@link https://docs.itick.org/rest-api/crypto/crypto-tick Official Documentation} for complete supported list
     * @param params.code - Symbol code (e.g., BTCUSDT, ETHUSDT etc.)
     * @returns Promise wrapped standard API response, data is real-time market data {@link TickData}
     * @example
     * ```typescript
     * // Get real-time trade market data for BTCUSDT cryptocurrency pair
     * getTick({ region: 'BA', code: 'BTCUSDT' }).then(response => {
     *     console.log('Real-time market:', response.data);
     *   }).catch(err => {
     *     console.error('Failed to get market data:', err);
     *   });
     * ```
     */
    async getTick(params: { region: string, code: string }): Promise<APIResponse<TickData>> {
        const { region, code } = params;
        return this._get('/crypto/tick', { region, code });
    }

    /**
     * Get Cryptocurrency Latest Quote
     * @description Query latest quote real-time market data for cryptocurrency based on market code and symbol code
     * @param params - Query parameters object
     * @param params.region - Market code (e.g., BA, BT, PB etc.), see {@link https://docs.itick.org/rest-api/crypto/crypto-quote Official Documentation} for complete supported list
     * @param params.code - Symbol code (e.g., BTCUSDT, ETHUSDT etc.)
     * @returns Promise wrapped API response, data is latest quote data {@link QuoteData}
     * @example
     * ```typescript
     * // Get latest quote for BTCUSDT cryptocurrency pair
     * getQuote({ region: 'BA', code: 'BTCUSDT' }).then(response => {
     *    console.log('Latest quote:', response.data);
     * }).catch(err => {
     *   console.error('Failed to get quote:', err);
     * });
     * ```
     */
    async getQuote(params: { region: string, code: string }): Promise<APIResponse<QuoteData>> {
        const { region, code } = params;
        return this._get('/crypto/quote', { region, code });
    }

    /**
     * Get Cryptocurrency Latest Order Book
     * @description Query latest order book data for cryptocurrency based on market code and symbol code
     * @param params - Query parameters object
     * @param params.region - Market code (e.g., BA, BT, PB etc.), see {@link https://docs.itick.org/rest-api/crypto/crypto-depth Official Documentation} for complete supported list
     * @param params.code - Symbol code (e.g., BTCUSDT, ETHUSDT etc.)
     * @returns Promise wrapped API response, data is latest order book data {@link DepthData}
     * @example
     * ```typescript
     * // Get latest order book for BTCUSDT cryptocurrency pair
     * getDepth({ region: 'BA', code: 'BTCUSDT' }).then(response => {
     *   console.log('Latest order book:', response.data);
     * }).catch(err => {
     *  console.error('Failed to get order book:', err);
     * });
     * ```
     */
    async getDepth(params: { region: string, code: string }): Promise<APIResponse<DepthData>> {
        const { region, code } = params;
        return this._get('/crypto/depth', { region, code });
    }

    /**
     * Get Cryptocurrency K-Line Data
     * @description Query cryptocurrency K-line data based on market code, symbol code, K-line period, end time and number of records to return
     * @param options - Query parameters object see {@link GetKlineOptions}
     * @param options.region - Market code (e.g., BA, BT, PB etc.), see {@link https://docs.itick.org/rest-api/crypto/crypto-kline Official Documentation} for complete supported list
     * @param params.code - Symbol code (e.g., BTCUSDT, ETHUSDT etc.)
     * @returns Promise wrapped API response, data is K-line data array {@link KlineData}
     * @example
     * ```typescript
     * // Get 5-minute K-line data for BTCUSDT cryptocurrency pair
     * getKline({ region: 'BA', code: 'BTCUSDT', interval: '5m', limit: 100 }).then(response => {
     *   console.log('K-line data:', response.data);
     * }).catch(err => {
     *  console.error('Failed to get K-line data:', err);
     * });
     * ```
     */
    async getKline(options: GetKlineOptions): Promise<APIResponse<KlineData[]>> {
        const { region, code, kType, interval, limit, et } = options;
        const params: Record<string, any> = { region, code, kType: convertKlinePeriod(interval ?? kType), limit };
        if (et) params.et = et;
        return this._get('/crypto/kline', params);
    }

    /**
     * Get Multiple Cryptocurrencies Latest Trades
     * @description Query latest trade real-time market data for multiple cryptocurrencies based on market code and symbol code list
     * @param params - Query parameters object
     * @param params.region - Market code (e.g., BA, BT, PB etc.), see {@link https://docs.itick.org/rest-api/crypto/crypto-ticks Official Documentation} for complete supported list
     * @param params.codes - Symbol code list (e.g., BTCUSDT, ETHUSDT etc.)
     * @returns Promise wrapped API response, data is multiple cryptocurrencies latest trade data object {@link TickDataMap}
     * @example
     * ```typescript
     * // Get latest trade market data for BTCUSDT and ETHUSDT cryptocurrency pairs
     * getTicks({ region: 'BA', codes: ['BTCUSDT', 'ETHUSDT'] }).then(response => {
     *  console.log('Latest trades:', response.data);
     * }).catch(err => {
     * console.error('Failed to get trade market data:', err);
     * });
     * ```
     */
    async getTicks(params: { region: string, codes: string[] | string }): Promise<APIResponse<TickDataMap>> {
        const { region, codes } = params;
        return this._get('/crypto/ticks', { region, codes: Array.isArray(codes) ? codes.join(',') : codes });
    }

    /**
     * Get Multiple Cryptocurrencies Latest Quotes
     * @description Query latest quote real-time market data for multiple cryptocurrencies based on market code and symbol code list
     * @param params - Query parameters object
     * @param params.region - Market code (e.g., BA, BT, PB etc.), see {@link https://docs.itick.org/rest-api/crypto/crypto-quotes Official Documentation} for complete supported list
     * @param params.codes - Symbol code list (e.g., BTCUSDT, ETHUSDT etc.)
     * @returns Promise wrapped API response, data is multiple cryptocurrencies latest quote data object {@link QuoteDataMap}
     * @example
     * ```typescript
     * // Get latest quotes for BTCUSDT and ETHUSDT cryptocurrency pairs
     * getQuotes({ region: 'BA', codes: ['BTCUSDT', 'ETHUSDT'] }).then(response => {
     * console.log('Latest quotes:', response.data);
     * }).catch(err => {
     * console.error('Failed to get quotes:', err);
     * });
     * ```
     */
    async getQuotes(params: { region: string, codes: string[] | string }): Promise<APIResponse<QuoteDataMap>> {
        const { region, codes } = params;
        return this._get('/crypto/quotes', { region, codes: Array.isArray(codes) ? codes.join(',') : codes });
    }

    /**
     * Get Multiple Cryptocurrencies Latest Order Books
     * @description Query latest order book real-time market data for multiple cryptocurrencies based on market code and symbol code list
     * @param params - Query parameters object
     * @param params.region - Market code (e.g., BA, BT, PB etc.), see {@link https://docs.itick.org/rest-api/crypto/crypto-depths Official Documentation} for complete supported list
     * @param params.codes - Symbol code list (e.g., BTCUSDT, ETHUSDT etc.)
     * @returns Promise wrapped API response, data is multiple cryptocurrencies latest order book data object {@link DepthDataMap}
     * @example
     * ```typescript
     * // Get latest order books for BTCUSDT and ETHUSDT cryptocurrency pairs
     * getDepths({ region: 'BA', codes: ['BTCUSDT', 'ETHUSDT'] }).then(response => {
     * console.log('Latest order books:', response.data);
     * }).catch(err => {
     * console.error('Failed to get order books:', err);
     * });
     * ```
     */
    async getDepths(params: { region: string, codes: string[] | string }): Promise<APIResponse<DepthDataMap>> {
        const { region, codes } = params;
        return this._get('/crypto/depths', { region, codes: Array.isArray(codes) ? codes.join(',') : codes });
    }

    /**
     * Get Multiple Cryptocurrencies K-Line Data
     * @description Query K-line data for multiple cryptocurrencies based on market code, symbol code list, K-line period, end time and number of records to return
     * @param options - Query parameters object {@link GetKlinesOptions}
     * @param options.region - Market code (e.g., BA, BT, PB etc.), see {@link https://docs.itick.org/rest-api/crypto/crypto-klines Official Documentation} for complete supported list
     * @param params.codes - Symbol code list (e.g., BTCUSDT, ETHUSDT etc.)
     * @returns Promise wrapped API response, data is multiple cryptocurrencies K-line data object {@link KlineDataMap}
     * @example
     * ```typescript
     * // Get 5-minute K-line data for BTCUSDT and ETHUSDT cryptocurrency pairs
     * getKlines({ region: 'BA', codes: ['BTCUSDT', 'ETHUSDT'], interval: '5m', limit: 100 }).then(response => {
     *  console.log('K-line data:', response.data);
     * }).catch(err => {
     * console.error('Failed to get K-line data:', err);
     * });
     * ```
     */
    async getKlines(options: GetKlinesOptions): Promise<APIResponse<KlineDataMap>> {
        const { region, codes, kType, interval, limit, et } = options;
        const params: Record<string, any> = { region, codes: Array.isArray(codes) ? codes.join(',') : codes, kType: convertKlinePeriod(interval ?? kType), limit };
        if (et) params.et = et;
        return this._get('/crypto/klines', params);
    }
    /**
     * Create WebSocket Connection
     * @description Creates a WebSocket connection and returns a SocketClient object for managing connection and subscribing to real-time data. Subscription data can be specified through options parameter.
     * Automatically handles connection retry and heartbeat mechanism to ensure connection stability and reliability.
     * @param options - Optional options object for creating WebSocket connection {@link CreateSocketOptions}
     * @returns SocketClient object for managing WebSocket connection
     * @example
     * ```typescript
     * // Create a WebSocket connection and subscribe to real-time trades, quotes, order books, 1-minute K-line data for BTCUSDT and ETHUSDT, with maximum reconnection attempts of 5
     * const client = new CryptoClient(token);
     * const socket = client.createSocket({
     *  maxReconnectTimes: 5,
     *  subscribeData: {
     *    codes: ['BTCUSDT$BA', 'ETHUSDT$BA'],
     *    types: ['tick','quote','depth','kline@1m'],
     *  }
     * });
     * socket.onSocketMessage(data => {
     *  console.log('Received WebSocket message:', data);
     * });
     * ```
     * @see Official Documentation https://docs.itick.org/websocket/crypto
     */
    createSocket(options?: CreateSocketOptions): SocketClient {
        return this._createSocket('/crypto', options);
    }
}
